import { useCallback, useEffect, useRef, useState } from 'react';

import { parseScreenRequest, type MissionScreenRequest } from '../lib/mission/screenRequest';
import { useOnMissionsChanged } from '../lib/mission/missionsChanged';
import { supabase } from '../lib/supabase';

/**
 * The gesture a mission waits for on a screen (`get_mission_screen_request`), or null when Otto asked
 * something else. Re-read when the missions change (an answer, a new question): nothing else moves it.
 */
export function useMissionScreenRequest(threadId: string | null) {
  const [request, setRequest] = useState<MissionScreenRequest | null>(null);
  const [loading, setLoading] = useState(Boolean(threadId));
  const [error, setError] = useState<string | null>(null);
  /** The mission the state above was read for: until it is the current one, it is still loading. */
  const [readFor, setReadFor] = useState<string | null>(null);
  const current = useRef(threadId);
  useEffect(() => {
    current.current = threadId;
  }, [threadId]);

  const load = useCallback(async (silent: boolean) => {
    if (!threadId) {
      setRequest(null);
      setLoading(false);
      setReadFor(null);
      return;
    }
    if (!silent) setLoading(true);
    // Not in the generated Supabase types yet (same escape hatch as useMissionCallRequest).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error: qErr } = await (supabase.rpc as any)('get_mission_screen_request', { p_thread_id: threadId });
    if (current.current !== threadId) return;   // another mission is shown since: its own read wins
    if (qErr) {
      setError('Impossible de charger la demande de geste.');
    } else {
      setError(null);
      setRequest(parseScreenRequest(data));
    }
    setReadFor(threadId);
    setLoading(false);
  }, [threadId]);

  useEffect(() => {
    void load(false);
  }, [load]);

  useOnMissionsChanged(() => void load(true));

  const stale = threadId !== null && readFor !== threadId;
  return { request: stale ? null : request, loading: loading || stale, error: stale ? null : error };
}
